import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../lib/lang";
import Logo from "./Logo";

export default function Nav() {
  const { lang, setLang, t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/classes", label: t.nav.classes },
    { to: "/faqs", label: t.nav.faqs },
    { to: "/contact", label: t.nav.contact },
  ];
  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));

  return (
    <header className="sticky top-0 z-50 border-b border-[#E5DECF] bg-[#FDFBF7]/85 backdrop-blur-xl">
      <nav className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-3" aria-label="Main navigation">
        <Link to="/" data-testid="nav-home-link" aria-label="Home">
          <Logo name={t.brand.name} role={t.brand.role} />
        </Link>

        <div className="hidden items-center gap-7 md:flex">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
              className={`text-sm font-semibold transition-colors duration-200 ${
                isActive(l.to) ? "text-saffron" : "text-ink hover:text-saffron"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => setLang(lang === "en" ? "hi" : "en")}
            data-testid="lang-toggle"
            className={`shrink-0 whitespace-nowrap rounded-full border border-[#E5DECF] bg-cream px-4 py-2 text-[13px] font-bold text-ink transition-colors duration-200 hover:border-saffron hover:text-saffron ${lang === "hi" ? "font-hi" : ""}`}
          >
            <span className="sm:hidden">{t.nav.langShort}</span>
            <span className="hidden sm:inline">{t.nav.langLabel}</span>
          </button>
          <Link to="/contact" data-testid="nav-cta" className="btn-primary hidden !px-5 !py-2.5 md:inline-flex">
            {t.nav.cta}
          </Link>
          <button
            className="rounded-full p-2 text-ink md:hidden"
            onClick={() => setOpen(true)}
            aria-label={t.nav.openMenu}
            data-testid="nav-menu-open-button"
          >
            <Menu size={26} />
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[70] flex flex-col bg-cream md:hidden"
            data-testid="nav-mobile-drawer"
          >
            <div className="flex items-center justify-between px-5 py-3">
              <Logo name={t.brand.name} role={t.brand.role} />
              <button
                className="rounded-full p-2 text-ink"
                onClick={() => setOpen(false)}
                aria-label={t.nav.closeMenu}
                data-testid="nav-menu-close-button"
              >
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-1 flex-col justify-center gap-2 px-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i, duration: 0.4 }}
                >
                  <Link
                    to={l.to}
                    data-testid={`drawer-link-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className={`block py-3 font-display text-4xl ${isActive(l.to) ? "text-saffron" : "text-ink"}`}
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="mt-6"
              >
                <Link to="/contact" data-testid="drawer-cta" className="btn-saffron w-full">
                  {t.nav.cta}
                </Link>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
